// Builds a deck.gl IconLayer for container markers from raw GeoJSON features.
import { IconLayer } from '@deck.gl/layers'
import get from 'lodash/get'

import { isPlacement } from 'components/inventory/containers/utils'
import { COMMON } from 'theme/colors'
import { getMeasureUnit } from 'utils/get-measure-unit'

import { resolveContainerColors } from './containerMarkerColors'
import { getContainerIconCanvas } from './generators/containerIconRaster'
import { generateContainerPillIcon, CONTAINER_PILL_CSS_HEIGHT } from './generators/generateContainerPillIcon'

import type { ContainerMarkerProperties } from './containerMarkerColors'
import type { ContainerPillIcon } from './generators/generateContainerPillIcon'
import type { Color } from '@deck.gl/core'
import type { IconLayerProps } from '@deck.gl/layers'
import type { Theme } from '@mui/material/styles'

// Feature properties are the open bag each map surface builds; the typed part is what the pill reads.
export type ContainerMarkerFeatureProperties = ContainerMarkerProperties &
  Record<string, unknown> & {
    // Private per-feature channels callers use to restyle a pill without inventing a status.
    _outlineColor?: string | null
    _outlineWidth?: number | null
    _hideLabel?: boolean | null
    _isExcluded?: boolean | null
  }

export type ContainerMarkerFeature = {
  geometry?: { coordinates?: unknown } | null
  properties?: ContainerMarkerFeatureProperties | null
}

export type ContainerMarkerRow = {
  feature: ContainerMarkerFeature
  position: [number, number]
  iconKey: string
}

export type ContainerMarkerLayerArgs = {
  features: ContainerMarkerFeature[]
  theme: Theme
  onHover?: IconLayerProps<ContainerMarkerRow>['onHover']
  onClick?: IconLayerProps<ContainerMarkerRow>['onClick']
}

type PillIcon = ContainerPillIcon & { anchorX: number; anchorY: number }
type AtlasEntry = { key: string; ic: PillIcon }
type PlacedEntry = AtlasEntry & { x: number; y: number }
type AtlasIcon = { x: number; y: number; width: number; height: number; anchorX: number; anchorY: number; mask: boolean }
type AtlasIconMapping = Record<string, AtlasIcon>
type IconAtlas = NonNullable<IconLayerProps['iconAtlas']>
type Atlas = { iconAtlas: IconAtlas; iconMapping: AtlasIconMapping }

const MAX_ATLAS_DIM = 4096
// IconLayer multiplies texture alpha by getColor alpha, so alpha fades a non-mask icon; RGB is ignored at mask:false.
const OPAQUE: Color = [255, 255, 255, 255]
// Low enough to read as "not part of this" beside a solid neighbour, high enough to stay legible and clickable.
const EXCLUDED: Color = [255, 255, 255, 90]
const iconCache = new Map<string, PillIcon>()
// A fresh atlas canvas per call makes deck.gl re-upload the texture — a one-frame gap that reads as flicker — so equal icon sets share one.
const ATLAS_CACHE_MAX = 32
const atlasCache = new Map<string, Atlas>()

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

// deck uploads any canvas as a texture at runtime; its types only name `string | Texture`.
const asIconAtlas = (canvas: HTMLCanvasElement) => canvas as unknown as IconAtlas

function getLabel(properties: ContainerMarkerFeatureProperties): string {
  // The caller's label-thinning channel: the pill keeps its chrome, only the text goes.
  if (get(properties, '_hideLabel')) return ''
  const volume = get(properties, 'measure.volume') || get(properties, 'measureVolume') || get(properties, 'volume')
  const unit = get(properties, 'measure.unit') || get(properties, 'measureUnit') || get(properties, 'unit')
  if (volume) return `${volume} ${getMeasureUnit(unit)}`.trim()
  const name = String(
    get(properties, 'containerName') || get(properties, 'configuredServiceContainerName') || get(properties, 'name') || ''
  )
  if (!name) return ''
  const match = name.match(/#(\d+)/)
  return match ? match[1] : name
}

function getIconKey(properties: ContainerMarkerFeatureProperties, themeKey: string): string {
  const type = get(properties, 'containerType.type') || get(properties, 'containerType', '')
  const status = get(properties, 'status', '')
  const colorHex = get(properties, 'color.color') || (typeof get(properties, 'color') === 'string' ? get(properties, 'color') : '')
  const label = getLabel(properties)
  const outline = get(properties, '_outlineColor') ?? ''
  const border = get(properties, '_pillBorderColor') ?? ''
  const outlineWidth = get(properties, '_outlineWidth') ?? ''
  // Placement is part of the key: it changes the border to dashed without changing any field above.
  return `${themeKey}|${type}|${status}|${colorHex}|${label}|${outline}|${border}|${outlineWidth}|${isPlacement(properties)}`
}

function getTypeKey(properties: ContainerMarkerFeatureProperties) {
  return get(properties, 'containerType.type') || get(properties, 'containerType')
}

function packAtlas(entries: AtlasEntry[]): Atlas {
  if (entries.length === 0) {
    return { iconAtlas: asIconAtlas(createCanvas(1, 1)), iconMapping: {} }
  }

  // Sorted so feature order can't miss the cache; iconCache pins each key to one canvas, so equal key sets give identical atlases.
  const sortedKeys = entries.map(e => e.key).sort()
  const cacheKey = sortedKeys.join('|')
  const cached = atlasCache.get(cacheKey)
  if (cached) return cached

  let rowX = 0
  let rowY = 0
  let rowH = 0
  let atlasW = 0

  const placed: PlacedEntry[] = entries.map(e => {
    if (rowX + e.ic.width > MAX_ATLAS_DIM && rowX > 0) {
      rowY += rowH
      rowX = 0
      rowH = 0
    }
    const entry = { ...e, x: rowX, y: rowY }
    rowX += e.ic.width
    if (e.ic.height > rowH) rowH = e.ic.height
    if (rowX > atlasW) atlasW = rowX
    return entry
  })

  const atlasH = rowY + rowH
  const atlas = createCanvas(atlasW, atlasH)
  const ctx = atlas.getContext('2d')
  const iconMapping: AtlasIconMapping = {}
  placed.forEach(({ key, ic, x, y }) => {
    ctx?.drawImage(ic.canvas, x, y)
    iconMapping[key] = { x, y, width: ic.width, height: ic.height, anchorX: ic.anchorX, anchorY: ic.anchorY, mask: false }
  })
  const result = { iconAtlas: asIconAtlas(atlas), iconMapping }
  if (atlasCache.size >= ATLAS_CACHE_MAX) {
    // Evict the oldest entry (Map preserves insertion order).
    const oldestKey = atlasCache.keys().next().value
    if (oldestKey !== undefined) atlasCache.delete(oldestKey)
  }
  atlasCache.set(cacheKey, result)
  return result
}

export function buildContainerMarkerLayer({ features, theme, onHover, onClick }: ContainerMarkerLayerArgs) {
  const entriesByKey = new Map<string, AtlasEntry>()
  const themeKey: string = get(theme, 'palette.mode', 'default')
  const outlineColor = themeKey === 'dark' ? COMMON.white : null
  const rows = features
    .map((feature): ContainerMarkerRow | null => {
      const coordinates = feature.geometry?.coordinates
      if (!Array.isArray(coordinates)) return null
      const lng = parseFloat(String(coordinates[0]))
      const lat = parseFloat(String(coordinates[1]))
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null
      const properties: ContainerMarkerFeatureProperties = feature.properties ?? {}
      const iconKey = getIconKey(properties, themeKey)
      let ic = iconCache.get(iconKey)
      if (!ic) {
        const colors = resolveContainerColors(properties, theme)
        const iconCanvas = getContainerIconCanvas(getTypeKey(properties))
        const label = getLabel(properties)
        const { canvas, width, height } = generateContainerPillIcon({
          label,
          iconCanvas,
          outerColor: colors.outerColor,
          innerBgColor: colors.innerBgColor,
          labelColor: colors.labelColor,
          iconBgColor: colors.iconBgColor,
          iconColor: colors.iconColor,
          // Per-feature outline (selection / pending-change halos) wins over the theme default.
          outlineColor: get(properties, '_outlineColor') || outlineColor,
          // Focused pins ask for a wider ring; everything else keeps the 2px default.
          outlineWidth: get(properties, '_outlineWidth') ?? undefined,
          dashed: colors.dashed,
        })
        ic = { canvas, width, height, anchorX: width / 2, anchorY: height }
        iconCache.set(iconKey, ic)
      }
      if (!entriesByKey.has(iconKey)) {
        entriesByKey.set(iconKey, { key: iconKey, ic })
      }
      return { feature, position: [lng, lat], iconKey }
    })
    .filter((row): row is ContainerMarkerRow => row !== null)
  const { iconAtlas, iconMapping } = packAtlas(Array.from(entriesByKey.values()))

  return new IconLayer<ContainerMarkerRow>({
    id: 'master-route-container-markers',
    data: rows,
    pickable: true,
    iconAtlas,
    iconMapping,
    getPosition: d => d.position,
    getIcon: d => d.iconKey,
    getColor: d => (get(d.feature, 'properties._isExcluded') ? EXCLUDED : OPAQUE),
    getSize: CONTAINER_PILL_CSS_HEIGHT,
    sizeUnits: 'pixels',
    onHover,
    onClick,
  })
}
